import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * ConsentContext
 *
 * Expose hai thứ:
 *  - services:   { "Google Analytics": true, "Meta Pixel": false, ... }   (per-service)
 *  - categories: { marketing: false, functional: true, statistics: false } (per-category)
 *
 * categories dựa vào categorySlug của từng service trong Usercentrics.
 * Một category được coi là "consented" khi TẤT CẢ service thuộc category đó đều được chấp nhận.
 */

const DEFAULT_CONTEXT = {
  services: {},
  categories: {
    functional: false,   // Functional thường luôn bật
    marketing: false,
    statistics: false,
  },
  isInitialized: false, // Usercentrics chưa load xong
};

const ConsentContext = createContext(DEFAULT_CONTEXT);

export const ConsentProvider = ({ children }) => {
  const [state, setState] = useState(DEFAULT_CONTEXT);

  useEffect(() => {
    // Đảm bảo không chạy lúc Gatsby đang build SSR
    if (typeof window === 'undefined') return;

    const updateConsents = async () => {
      const uc = window.UC_UI;
      console.log('[ConsentContext] updateConsents called, UC_UI:', !!uc);
      if (!uc) return;

      // getServicesBaseInfo() trả về Promise → phải await
      let services = await uc.getServicesBaseInfo();
      console.log('[ConsentContext] raw services:', services);

      if (!Array.isArray(services)) {
        services = Object.values(services || {});
        if (!Array.isArray(services)) services = [];
      }

      const serviceState = services.reduce((acc, service) => {
        if (service && service.name && service.consent) {
          acc[service.name] = service.consent.status;
        }
        return acc;
      }, {});

      const categoryBuckets = services.reduce((acc, service) => {
        if (!service || !service.consent) return acc;
        const slug = (service.categorySlug || 'unknown').toLowerCase();
        if (!acc[slug]) acc[slug] = [];
        acc[slug].push(service.consent.status);
        return acc;
      }, {});

      console.log('[ConsentContext] categoryBuckets:', categoryBuckets);

      const categoryState = Object.entries(categoryBuckets).reduce(
        (acc, [slug, statuses]) => {
          acc[slug] = statuses.length > 0 && statuses.every(Boolean);
          return acc;
        },
        {}
      );

      console.log('[ConsentContext] categoryState:', categoryState);

      setState({
        services: serviceState,
        categories: {
          essential: categoryState['essential'] ?? true,
          functional: categoryState['functional'] ?? categoryState['essential'] ?? true,
          marketing: categoryState['marketing'] ?? false,
          statistics:
            categoryState['statistics'] ??
            categoryState['analytics'] ??
            false,
        },
        isInitialized: true,
      });
    };

    window.addEventListener('UC_UI_INITIALIZED', updateConsents);
    window.addEventListener('UC_UI_CMP_EVENT', updateConsents);

    // Fix race condition: Nếu UC_UI đã load xong trước khi React mount
    // thì UC_UI_INITIALIZED đã fire rồi → listener không bắt được → gọi thủ công ngay
    if (window.UC_UI && typeof window.UC_UI.getServicesBaseInfo === 'function') {
      updateConsents();
    }

    return () => {
      window.removeEventListener('UC_UI_INITIALIZED', updateConsents);
      window.removeEventListener('UC_UI_CMP_EVENT', updateConsents);
    };
  }, []);

  return (
    <ConsentContext.Provider value={state}>
      {children}
    </ConsentContext.Provider>
  );
};

export const useConsent = () => useContext(ConsentContext);